import { isThemeStudioDemoBuild } from '../themeStudioDemo/buildMode';
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { setAuthToken, clearAuthToken, getAuthGeneration, getAuthToken } from '../utils/auth';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import {
  AUTH_UNAUTHORIZED_EVENT,
  type AuthExitReason,
  type AuthSyncEvent,
  type AuthUnauthorizedEventDetail,
  broadcastAuthSyncEvent,
  clearStoredAuthExitReason,
  getStoredAuthExitReason,
  rememberAuthExitReason,
  subscribeToAuthSyncEvents,
} from '../utils/authSession';
import { getPrimaryCohortRole } from '../utils/cohortSelection';
import { events, user as userApi, auth as authApi } from '../services/api';
import { buildAnalyticsEventPayload } from 'utils/analyticsEventContext';
import { isEmbeddedMeetingPanelPath } from 'utils/meetingPanelRoutes';
import {
  type AuthRecoverySnapshot,
  authRecoverySnapshotRequiresHandshake,
  getAuthRevocationSafetyError,
  subscribeToAuthRevocationState,
} from 'utils/authTransitions';
import { clearSnapshotDueDismissal } from 'utils/snapshotSession';
import { clearNativeMobileSession } from 'mobile/mobileNotifications';

interface User {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
  is_staff: boolean;
  has_completed_intake?: boolean; // Now optional since we use the flow completion endpoint
  timezone?: string;
  profile_color?: string | null;
  avatar_url?: string | null;
  debug_enabled?: boolean;
  settings?: {
    question_flow_tts_enabled?: boolean;
    question_flow_tts_available?: boolean;
    voice_mode_voice?: string;
    voice_mode_personality?: string;
    voice_mode_response_length?: string;
    voice_mode_follow_up_intensity?: string;
  };
  role?: 'mentor' | 'mentee' | 'cohort_director' | 'cohort_admin';
  organization?: {
    id: string;
    name: string;
    org_id: string;
    icon_url?: string | null;
  } | null;
  organization_role?: 'user' | 'admin';
  cohorts?: {
    id: string;
    name: string;
    is_active: boolean;
    is_current?: boolean;
    role: 'mentee' | 'mentor' | 'cohort_director' | 'cohort_admin';
    mentee_label_singular?: string | null;
    mentee_label_plural?: string | null;
    mentor_label_singular?: string | null;
    mentor_label_plural?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  }[];
}

interface LoginOptions {
  method?: string;
  metadata?: Record<string, any>;
  recoveryConfirmed?: boolean;
  recoverySnapshot?: AuthRecoverySnapshot;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isAuthLoading: boolean; // Add loading state
  authExitReason: AuthExitReason | null;
  clearAuthExitReason: () => void;
  login: (token: string, userData: User, options?: LoginOptions) => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const shouldSkipAuthBootstrap = () => {
  return isEmbeddedMeetingPanelPath();
};

const getErrorStatus = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  return (error as { response?: { status?: number } }).response?.status;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true); // Initialize loading state
  const [authExitReason, setAuthExitReason] = useState<AuthExitReason | null>(() =>
    getStoredAuthExitReason(),
  );
  const [isBootstrapUnavailable, setIsBootstrapUnavailable] = useState(false);
  const [isBootstrapRetrying, setIsBootstrapRetrying] = useState(false);
  const retryBootstrap = useRef<() => void>(() => undefined);
  const hasStartedAuthBootstrap = useRef(false);
  const isAuthBootstrapActive = useRef(false);
  const authStateGeneration = useRef(0);

  const deriveRole = (cohorts: User['cohorts'] | undefined): User['role'] => {
    const role = getPrimaryCohortRole(cohorts);
    return role as User['role'] | undefined;
  };

  const clearAuthExitReason = useCallback(() => {
    clearStoredAuthExitReason();
    setAuthExitReason(null);
  }, []);

  const applySignedOutState = useCallback(
    (reason: AuthExitReason | null, options?: { broadcastType?: AuthSyncEvent['type'] }) => {
      authStateGeneration.current += 1;
      setIsBootstrapUnavailable(false);
      clearNativeMobileSession();
      clearAuthToken();
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthLoading(false);

      if (reason) {
        rememberAuthExitReason(reason);
        setAuthExitReason(reason);
      } else {
        clearStoredAuthExitReason();
        setAuthExitReason(null);
      }

      if (options?.broadcastType) {
        broadcastAuthSyncEvent(options.broadcastType);
      }
    },
    [],
  );

  useEffect(() => {
    isAuthBootstrapActive.current = true;
    if (hasStartedAuthBootstrap.current) {
      return () => {
        isAuthBootstrapActive.current = false;
      };
    }
    hasStartedAuthBootstrap.current = true;
    const bootstrapGeneration = authStateGeneration.current;
    const isCurrentBootstrap = () => (
      isAuthBootstrapActive.current &&
      authStateGeneration.current === bootstrapGeneration
    );

    let canRetry = false;
    let requestPending = false;
    const verifySession = async () => {
      if (shouldSkipAuthBootstrap()) {
        if (isCurrentBootstrap()) {
          setUser(null);
          setIsAuthLoading(false);
        }
        return;
      }

      try {
        await authApi.waitForPendingLogout();
      } catch (error) {
        if (isCurrentBootstrap()) {
          applySignedOutState(null);
        }
        return;
      }
      if (!isCurrentBootstrap()) return;
      if (authApi.requiresLogoutRecovery()) {
        if (isCurrentBootstrap()) {
          applySignedOutState(null);
        }
        return;
      }

      const userDataString = localStorage.getItem('user');
      let storedUser: User | null = null;

      if (userDataString) {
        try {
          storedUser = JSON.parse(userDataString) as User;
          // Cached identity is only a display fallback after server verification.
          // In particular, never expose cached roles while offline.
        } catch (error) {
          console.error('AuthContext: Error parsing user data from localStorage:', error);
          localStorage.removeItem('user');
        }
      }

      const requestAuthGeneration = getAuthGeneration();
      const requestUserCache = localStorage.getItem('user');
      const hadAuthenticationState = Boolean(getAuthToken() || requestUserCache);
      const acceptCurrentResponse = () => {
        if (!isCurrentBootstrap()) return false;
        if (getAuthGeneration() === requestAuthGeneration
          && localStorage.getItem('user') === requestUserCache) return true;
        // Another tab can normalize the cache without a login/logout event.
        // Reject this response, but keep a usable way to verify the new session.
        canRetry = true;
        setUser(null);
        setIsBootstrapUnavailable(true);
        return false;
      };

      try {
        const profile = await userApi.getMe({ expectedAnonymousProfileProbe: true });
        if (!acceptCurrentResponse()) return;
        if (storedUser?.id !== profile.id) storedUser = null;
        const normalizedUser: User = {
          id: profile.id,
          username: profile.username ?? '',
          first_name: profile.first_name ?? storedUser?.first_name,
          last_name: profile.last_name ?? storedUser?.last_name,
          is_staff:
            typeof profile.is_admin === 'boolean'
              ? profile.is_admin
              : false,
          organization: profile.organization
            ? {
                id: profile.organization.id,
                name: profile.organization.name,
                org_id: profile.organization.org_id,
                icon_url:
                  typeof profile.organization.icon_url === 'string' || profile.organization.icon_url === null
                    ? profile.organization.icon_url
                    : storedUser?.organization?.icon_url ?? null,
              }
            : null,
          organization_role: profile.organization_role === 'admin' ? 'admin' : 'user',
          cohorts: Array.isArray(profile.cohorts)
            ? profile.cohorts.map((cohort: any) => ({
                id: cohort.id,
                name: cohort.name,
                is_active: Boolean(cohort.is_active),
                is_current: Boolean(cohort.is_current),
                role:
                  cohort.role === 'mentor' ||
                  cohort.role === 'mentee' ||
                  cohort.role === 'cohort_director' ||
                  cohort.role === 'cohort_admin'
                    ? cohort.role
                    : 'mentee',
                start_date: cohort.start_date ?? null,
                end_date: cohort.end_date ?? null,
                mentee_label_singular: cohort.mentee_label_singular ?? null,
                mentee_label_plural: cohort.mentee_label_plural ?? null,
                mentor_label_singular: cohort.mentor_label_singular ?? null,
                mentor_label_plural: cohort.mentor_label_plural ?? null,
              }))
            : [],
          has_completed_intake:
            typeof profile.has_completed_intake === 'boolean'
              ? profile.has_completed_intake
              : storedUser?.has_completed_intake,
          timezone: profile.timezone ?? storedUser?.timezone,
          profile_color:
            typeof profile.profile_color === 'string' || profile.profile_color === null
              ? profile.profile_color
              : storedUser?.profile_color ?? null,
          avatar_url:
            typeof profile?.profile?.avatar_url === 'string' || profile?.profile?.avatar_url === null
              ? profile.profile.avatar_url
              : storedUser?.avatar_url ?? null,
          debug_enabled:
            typeof profile.debug_enabled === 'boolean'
              ? profile.debug_enabled
              : false,
          settings: {
            question_flow_tts_enabled:
              typeof profile?.settings?.question_flow_tts_enabled === 'boolean'
                ? profile.settings.question_flow_tts_enabled
                : storedUser?.settings?.question_flow_tts_enabled ?? false,
            question_flow_tts_available:
              typeof profile?.settings?.question_flow_tts_available === 'boolean'
                ? profile.settings.question_flow_tts_available
                : storedUser?.settings?.question_flow_tts_available ?? false,
            voice_mode_voice:
              typeof profile?.settings?.voice_mode_voice === 'string'
                ? profile.settings.voice_mode_voice
                : storedUser?.settings?.voice_mode_voice ?? 'cedar',
            voice_mode_personality:
              typeof profile?.settings?.voice_mode_personality === 'string'
                ? profile.settings.voice_mode_personality
                : storedUser?.settings?.voice_mode_personality ?? 'warm_practical',
            voice_mode_response_length:
              typeof profile?.settings?.voice_mode_response_length === 'string'
                ? profile.settings.voice_mode_response_length
                : storedUser?.settings?.voice_mode_response_length ?? 'balanced',
            voice_mode_follow_up_intensity:
              typeof profile?.settings?.voice_mode_follow_up_intensity === 'string'
                ? profile.settings.voice_mode_follow_up_intensity
                : storedUser?.settings?.voice_mode_follow_up_intensity ?? 'standard',
          },
        };

        const userWithRole = {
          ...normalizedUser,
          role: deriveRole(normalizedUser.cohorts),
        };

        if (!isCurrentBootstrap()) {
          return;
        }
        localStorage.setItem('user', JSON.stringify(userWithRole));
        setUser(userWithRole);
        setIsBootstrapUnavailable(false);
        setIsAuthLoading(false);
      } catch (error: unknown) {
        if (!acceptCurrentResponse()) return;
        const status = getErrorStatus(error);
        if (status === undefined || status === 408 || status === 429 || status >= 500) {
          // A transport/server failure says nothing about credential validity.
          // Keep the cookie/token and native binding, but keep routes blocked.
          canRetry = true;
          setUser(null);
          setIsBootstrapUnavailable(true);
        } else if (hadAuthenticationState) {
          applySignedOutState('session_expired');
        } else {
          // An anonymous cookie probe can legitimately return 401. Do not end
          // a guest native call or announce session expiry for that probe.
          clearAuthToken();
          localStorage.removeItem('user');
          setUser(null);
          setIsBootstrapUnavailable(false);
          setIsAuthLoading(false);
        }
      }
    };

    const initializeAuth = async () => {
      if (!isCurrentBootstrap() || requestPending) return;
      requestPending = true;
      canRetry = false;
      setIsBootstrapRetrying(true);
      try {
        await verifySession();
      } finally {
        requestPending = false;
        if (isCurrentBootstrap()) setIsBootstrapRetrying(false);
      }
    };
    retryBootstrap.current = () => {
      if (canRetry) void initializeAuth();
    };
    void initializeAuth();
    return () => {
      isAuthBootstrapActive.current = false;
    };
  }, [applySignedOutState]);

  useEffect(() => {
    const retry = () => retryBootstrap.current();
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, []);

  useEffect(() => {
    return subscribeToAuthRevocationState((state) => {
      if (state === 'pending' || state === 'recovery') {
        applySignedOutState(null);
      }
    });
  }, [applySignedOutState]);

  useEffect(() => {
    const handleUnauthorized = (event: Event) => {
      const customEvent = event as CustomEvent<AuthUnauthorizedEventDetail>;
      const reason = customEvent.detail?.reason ?? 'session_expired';
      applySignedOutState(reason, {
        broadcastType: reason === 'password_changed' ? 'password_changed' : 'session_expired',
      });
    };

    window.addEventListener(
      AUTH_UNAUTHORIZED_EVENT,
      handleUnauthorized as EventListener,
    );
    return () => {
      window.removeEventListener(
        AUTH_UNAUTHORIZED_EVENT,
        handleUnauthorized as EventListener,
      );
    };
  }, [applySignedOutState]);

  useEffect(() => {
    return subscribeToAuthSyncEvents((event) => {
      // Bootstrap/recovery has no trusted user yet, but still belongs to the
      // outgoing session and must be invalidated by cross-tab changes.
      if (!user && !isAuthLoading) {
        return;
      }

      if (event.type === 'login') {
        applySignedOutState('signed_in_elsewhere');
        return;
      }

      if (event.type === 'logout') {
        applySignedOutState('signed_out_elsewhere');
        return;
      }

      if (event.type === 'session_expired') {
        applySignedOutState('session_expired');
      }
      if (event.type === 'password_changed') {
        applySignedOutState('password_changed');
      }
    });
  }, [applySignedOutState, user, isAuthLoading]);

  const login = useCallback(async (
    token: string,
    userData: User,
    options: LoginOptions = {},
  ) => {
    await authApi.waitForPendingLogout();
    if (!options.recoverySnapshot && authApi.requiresLogoutRecovery()) {
      throw getAuthRevocationSafetyError();
    }
    const recoverySnapshot = options.recoverySnapshot
      ?? authApi.captureLogoutRecoverySnapshot();
    const recoveryRequired = authRecoverySnapshotRequiresHandshake(recoverySnapshot);
    if (recoveryRequired && options.recoveryConfirmed !== true) {
      throw getAuthRevocationSafetyError();
    }

    try {
      authApi.assertLogoutRecoverySnapshotCurrent(recoverySnapshot);
      localStorage.setItem('user', JSON.stringify(userData));
      authApi.assertLogoutRecoverySnapshotCurrent(recoverySnapshot);

      authStateGeneration.current += 1;
      setIsBootstrapUnavailable(false);
      setAuthToken(token);
      clearSnapshotDueDismissal(userData.id);
      setUser(userData);
      setIsAuthLoading(false); // Ensure loading is set to false upon login
      clearStoredAuthExitReason();
      setAuthExitReason(null);
      if (recoveryRequired) {
        authApi.completeLogoutRecovery(recoverySnapshot);
      } else {
        // Re-check after the synchronous state application so a logout that
        // appeared after the backend response cannot expose this session.
        authApi.assertLogoutRecoverySnapshotCurrent(recoverySnapshot);
      }
    } catch (error) {
      authStateGeneration.current += 1;
      setIsBootstrapUnavailable(false);
      clearAuthToken();
      try {
        localStorage.removeItem('user');
      } catch {
        // Keep the recovery marker so the caller can retry in this lifecycle.
      }
      setUser(null);
      setIsAuthLoading(false);
      throw error;
    }
    broadcastAuthSyncEvent('login');

    const eventPayload = buildAnalyticsEventPayload({
      method: options.method ?? 'unknown',
      user_id: userData.id,
      ...options.metadata,
    }, userData);

    events.track('login', eventPayload).catch((error) => {
      console.error('AuthContext: Failed to track login event:', error);
    });
  }, []);

  const logout = () => {
    const currentUser = user;

    if (currentUser) {
      clearSnapshotDueDismissal(currentUser.id);
      events.trackLogout(buildAnalyticsEventPayload({
        user_id: currentUser.id,
      }, currentUser)).catch(() => undefined);
    }

    // Start both authenticated requests before clearing local state. The HTTP request
    // interceptor snapshots authentication synchronously, so a late response belongs
    // to this outgoing session and cannot invalidate a newer login.
    const serverLogoutRequest = authApi.logout();
    applySignedOutState(null, { broadcastType: 'logout' });

    void serverLogoutRequest.catch((error) => {
      console.error('AuthContext: Failed to revoke server session on logout:', error);
    });
  };

  const updateUser = useCallback((updates: Partial<User>) => {
    authStateGeneration.current += 1;
    setUser((prevUser) => {
      if (!prevUser) {
        return prevUser;
      }

      const updatedUser = { ...prevUser, ...updates };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  return (
    <AuthContext.Provider value={{
      isAuthenticated: !!user,
      user,
      isAuthLoading, // Expose loading state
      authExitReason,
      clearAuthExitReason,
      login,
      logout,
      updateUser,
    }}>
      {children}
      {isBootstrapUnavailable && (
        <div className="fixed inset-x-0 bottom-0 z-[1100] p-4" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
          <Alert className="mx-auto max-w-md bg-white text-slate-900 shadow-lg">
            <AlertTitle>Let’s reconnect</AlertTitle>
            <AlertDescription className="mt-2 text-slate-700">
              We couldn’t check your session. You haven’t been signed out. We’ll try again when your connection returns.
            </AlertDescription>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button type="button" size="auto" disabled={isBootstrapRetrying} onClick={() => retryBootstrap.current()}>
                {isBootstrapRetrying ? 'Checking session…' : 'Try again'}
              </Button>
              <Button type="button" size="auto" variant="outline" onClick={logout}>Sign out</Button>
            </div>
          </Alert>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Anonymous, inert context for the isolated staging sample player only.
 * This does not authorize normal app routes or mount the auth bootstrap.
 */
export const ThemeStudioAnonymousProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!isThemeStudioDemoBuild()) {
    throw new Error('Theme Studio samples are disabled in this build');
  }
  return <AuthContext.Provider value={{
    isAuthenticated: false, user: null, isAuthLoading: false, authExitReason: null,
    clearAuthExitReason: () => {}, logout: () => {}, updateUser: () => {},
    login: async () => { throw new Error('Sign-in is unavailable in the sample player'); },
  }}>{children}</AuthContext.Provider>;
};
